import EmptyState from "@/components/empty-state/EmptyState.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { DemoUnavailable } from "@/components/demo-unavailable/DemoUnavailable.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { WebhookEditModal } from "@/pages/settings/webhooks/widgets/WebhookEditModal.tsx";
import { WebhookSecretModal } from "@/pages/settings/webhooks/widgets/WebhookSecretModal.tsx";
import {
  useWebhookDelete,
  useWebhookRotateSecret,
  useWebhookSave,
  useWebhooks,
} from "@/queries/settings/use-webhooks.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import { ApiWebhook, ApiWebhookFilter, ApiWebhookFilterDefault } from "@/types/webhook.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  EmptyStateBase,
  FontAwesomeIcon,
  Page,
  PaginationBlock,
  Stack,
  Table,
  TableCell,
  Tag,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "react-use";

export function WebhooksPage() {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<ApiWebhookFilter>(ApiWebhookFilterDefault);
  const { data, isLoading, isFetching, isError, refetch } = useWebhooks(filter);
  const deleteWebhook = useWebhookDelete();
  const rotateSecret = useWebhookRotateSecret();
  const toggleWebhook = useWebhookSave();

  useTitle(t("core:webhooks"), t("common:settings"));
  useDebounce(
    () => setFilter((current) => ({ ...current, page_number: 1, search_text: searchTerm })),
    300,
    [searchTerm]
  );

  const openEdit = useCallback(
    (webhook?: ApiWebhook) => openModal(WebhookEditModal, { webhook }),
    [openModal]
  );

  const handleDelete = useCallback(
    (webhook: ApiWebhook) => {
      openModal(ConfirmationModal, {
        question: t("core:webhook_delete_title"),
        text: t("core:webhook_delete_description"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          try {
            await deleteWebhook.mutateAsync(webhook.id);
          } catch {
            return;
          }
        },
      });
    },
    [deleteWebhook, openModal]
  );

  const handleRotateSecret = useCallback(
    (webhook: ApiWebhook) => {
      openModal(ConfirmationModal, {
        question: t("core:webhook_rotate_secret"),
        text: t("core:webhook_rotate_secret_description"),
        acceptTitle: t("core:webhook_rotate_secret"),
        onAccept: async () => {
          try {
            const result = await rotateSecret.mutateAsync(webhook.id);
            openModal(WebhookSecretModal, { secret: result.signature_secret });
          } catch {
            return;
          }
        },
      });
    },
    [openModal, rotateSecret]
  );

  const handleToggleActive = useCallback(
    async (webhook: ApiWebhook) => {
      try {
        await toggleWebhook.mutateAsync({
          id: webhook.id,
          payload: { is_active: !webhook.is_active },
        });
      } catch {
        return;
      }
    },
    [toggleWebhook]
  );

  const columns = useMemo<ColumnDef<ApiWebhook>[]>(
    () => [
      {
        header: t("common:title"),
        cell: ({ row }) => (
          <TableCell caption={row.original.title ? row.original.url : undefined}>
            {row.original.title || row.original.url}
          </TableCell>
        ),
        accessorKey: "title",
        ...ColumnSizes.employee,
      },
      {
        header: t("common:status"),
        cell: ({ row }) => (
          <Stack items={"start"}>
            <Tag variant={row.original.is_active ? "positive" : "secondary"} appearance={"filled"}>
              {row.original.is_active
                ? t("workflow:status_is_active")
                : t("core:webhook_status_inactive")}
            </Tag>
          </Stack>
        ),
        accessorKey: "is_active",
        ...ColumnSizes.status,
      },
      {
        header: t("core:webhook_event_types"),
        cell: ({ row }) => (
          <TableCell variant={"secondary"}>
            {t("plurals:webhook_events_count", { count: row.original.event_types.length })}
          </TableCell>
        ),
        accessorKey: "event_types",
        ...ColumnSizes.date,
      },
    ],
    [t]
  );

  const renderRowAction = useCallback(
    (row: Row<ApiWebhook>) => (
      <DropdownMenu>
        <DropdownMenuItem
          title={t("core:webhook_delivery_history")}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-clock-rotate-left"} />}
          onClick={() => navigate(`/settings/webhooks/${row.original.id}/deliveries`)}
        />
        <DropdownMenuItem
          title={t("common:edit")}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
          onClick={() => openEdit(row.original)}
        />
        <DropdownMenuItem
          title={row.original.is_active ? t("common:disable") : t("common:enable")}
          leftIcon={
            <FontAwesomeIcon
              icon={row.original.is_active ? "fa-light fa-toggle-off" : "fa-light fa-toggle-on"}
            />
          }
          onClick={() => void handleToggleActive(row.original)}
        />
        <DropdownMenuItem
          title={t("core:webhook_rotate_secret") + "..."}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-arrows-rotate"} />}
          onClick={() => handleRotateSecret(row.original)}
        />
        <DropdownMenuItem
          title={t("common:delete") + "..."}
          leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
          variant={"menuDanger"}
          onClick={() => handleDelete(row.original)}
        />
      </DropdownMenu>
    ),
    [handleDelete, handleRotateSecret, handleToggleActive, navigate, openEdit, t]
  );

  return (
    <Page className="h-full">
      <Page.Header showBack title={t("core:webhooks")} navigateTo={"/settings"}>
        <Button leftIcon={<FontAwesomeIcon icon={"fa-light fa-plus"} />} onClick={() => openEdit()}>
          {t("common:create_new")}
        </Button>
      </Page.Header>
      <Page.Content stretch>
        <DemoUnavailable
          appearance="notice"
          label={t("integrations:demo_webhooks_title")}
          description={t("integrations:demo_webhooks_description")}
          className="mb-4"
        />
        <Page.StickyLayout
          toolbar={
            <Stack direction={"horizontal"}>
              <Search
                value={searchTerm}
                onInput={(event) => setSearchTerm(event.currentTarget.value)}
                placeholder={t("common:search")}
                className={"w-96"}
              />
            </Stack>
          }
          footer={
            <PaginationBlock
              metadata={data?.metadata}
              onPageChange={(page) => setFilter((current) => ({ ...current, page_number: page }))}
            />
          }
        >
          {isError ? (
            <EmptyStateBase
              emoji={":warning:"}
              title={t("core:webhook_load_error")}
              description={t("core:webhook_load_error_description")}
              primaryButton={
                <Button variant={"secondary"} onClick={() => void refetch()}>
                  {t("common:retry")}
                </Button>
              }
            />
          ) : (
            <Table
              stickyHeader
              renderRowAction={renderRowAction}
              data={data?.items ?? []}
              columns={columns}
              isFetching={
                isFetching ||
                deleteWebhook.isPending ||
                rotateSecret.isPending ||
                toggleWebhook.isPending
              }
              showSkeleton={isLoading}
              showNotFoundError={searchTerm !== "" && data?.metadata.total_items === 0}
              notFoundComponent={<EmptyStateNothingFound />}
              emptyStateComponent={<EmptyState />}
            />
          )}
        </Page.StickyLayout>
      </Page.Content>
    </Page>
  );
}
